import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Phone, MessageCircle, CheckCircle2, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import FAQAccordion from "@/components/FAQAccordion";
import AppointmentForm from "@/components/AppointmentForm";
import InsurersStrip from "@/components/InsurersStrip";
import { JsonLd, medicalProcedureSchema } from "@/components/StructuredData";
import { findService, SERVICES } from "@/data/services";
import { CLINIC } from "@/lib/clinicInfo";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = findService(slug);
  if (!service) return <Navigate to="/services" replace />;

  const related = SERVICES.filter((s) => s.slug !== service.slug).slice(0, 3);

  return (
    <>
      <Seo
        title={`${service.name} — ${service.tagline}`}
        description={`${service.name} at Nehchal Eye Centre, Sector 32 Noida by Dr. Gurkiran Kaur. ${service.short}`}
        path={`/services/${service.slug}`}
      />
      <JsonLd data={medicalProcedureSchema(service, CLINIC)} />
      <Breadcrumbs
        items={[
          { name: "Services", path: "/services" },
          { name: service.name, path: `/services/${service.slug}` },
        ]}
      />

      <section className="container-wide pt-10 pb-16">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-widest text-accent mb-3">Service</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-foreground leading-[1.05]">
            {service.name}
          </h1>
          <p className="mt-4 text-lg text-primary">{service.tagline}</p>
          <p className="mt-6 text-foreground/80 leading-8">{service.what}</p>

          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={`tel:${CLINIC.phoneRaw}`}
              className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm hover:bg-primary/90"
              data-testid="service-call-btn"
            >
              <Phone className="h-4 w-4" /> Call {CLINIC.phone}
            </a>
            <a
              href={CLINIC.whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm hover:bg-secondary"
              data-testid="service-whatsapp-btn"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
          </div>
        </div>
      </section>

      <section className="container-wide pb-16 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-10">
          <div>
            <div className="text-xs uppercase tracking-widest text-accent mb-3">What's Included</div>
            <h2 className="font-serif text-2xl sm:text-3xl text-foreground mb-6">
              What this service includes at our Noida clinic
            </h2>
            <ul className="space-y-3">
              {service.includes.map((i) => (
                <li key={i} className="flex items-start gap-3 text-foreground/85">
                  <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                  <span>{i}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="text-xs uppercase tracking-widest text-accent mb-3">Who Should Book</div>
            <h2 className="font-serif text-2xl sm:text-3xl text-foreground mb-4">Who this is for.</h2>
            <p className="text-foreground/80 leading-8">{service.who}</p>
          </div>

          <div className="rounded-2xl border border-primary/20 bg-primary/5 p-6 sm:p-8">
            <div className="text-xs uppercase tracking-widest text-primary mb-2">Ready to book?</div>
            <div className="font-serif text-2xl text-foreground mb-2">
              Consult Dr. Gurkiran Kaur for {service.name.toLowerCase()} in Noida.
            </div>
            <p className="text-sm text-foreground/70 mb-4">
              Located at 10, Marigold Court, Wave City Centre, Sector 32 Noida.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm hover:bg-primary/90"
              data-testid="service-book-cta"
            >
              Book Appointment <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>

        <aside className="lg:col-span-1">
          <div className="rounded-2xl border border-border bg-card p-6 sticky top-24">
            <div className="text-xs uppercase tracking-widest text-accent mb-3">Quick Enquiry</div>
            <div className="font-serif text-xl mb-4">Book {service.name}</div>
            <AppointmentForm defaultService={service.name} />
          </div>
        </aside>
      </section>

      <FAQAccordion faqs={service.faqs} title={`FAQs — ${service.name} in Noida`} />

      {service.slug === "cataract-surgery" ? <InsurersStrip compact /> : null}

      {/* Related */}
      <section className="container-wide pb-24">
        <div className="text-xs uppercase tracking-widest text-accent mb-3">Related Services</div>
        <h2 className="font-serif text-2xl sm:text-3xl text-foreground mb-8">You might also need.</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {related.map((s) => (
            <Link
              key={s.slug}
              to={`/services/${s.slug}`}
              className="rounded-xl border border-border bg-card hover:border-primary/40 p-5 transition-colors"
              data-testid={`related-service-${s.slug}`}
            >
              <div className="font-serif text-lg">{s.name}</div>
              <p className="text-sm text-foreground/65 mt-2">{s.short}</p>
            </Link>
          ))}
        </div>
      </section>
    </>
  );
}
